/**
 * Lobby "How to Play" panel — Survey Says version of WhatIs ui/lobby-howto.ts.
 * Numbered list explaining the viewer-facing flow (no buttons, no `!a` prefix
 * for Survey Says — plain chat is the answer).
 */

export interface LobbyHowToHandle {
  el: HTMLDivElement;
  dispose: () => void;
}

const STYLES = `
  .ss-howto {
    position: relative;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.75) 100%);
    border: 2px solid var(--sg-accent, #E91E63);
    border-radius: 14px;
    box-shadow: 0 0 30px rgba(233,30,99,0.18), 0 4px 30px rgba(0,0,0,0.4);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    pointer-events: auto;
  }
  .ss-howto-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 14px rgba(255,196,61,0.4);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(233,30,99,0.35);
    flex: 0 0 auto;
  }
  .ss-howto-body {
    flex: 0 0 auto;
    overflow: hidden;
    padding: 10px 14px 12px 14px;
  }
  .ss-howto-list {
    list-style: none;
    margin: 0;
    padding: 0;
    counter-reset: ss-howto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .ss-howto-item {
    counter-increment: ss-howto;
    position: relative;
    padding: 4px 0 4px 34px;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #E2E8F0;
  }
  .ss-howto-item::before {
    content: counter(ss-howto);
    position: absolute;
    left: 0;
    top: 2px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255,196,61,0.18);
    border: 1px solid var(--sg-primary, #FFC43D);
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    font-size: 0.85rem;
    text-align: center;
    line-height: 20px;
  }
  .ss-howto-item code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.12);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
    font-size: 0.9em;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('ss-howto-style')) return;
  const s = document.createElement('style');
  s.id = 'ss-howto-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobbyHowTo(mount: HTMLElement): LobbyHowToHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'ss-howto';
  el.setAttribute('data-role', 'lobby-howto');
  el.innerHTML = `
    <div class="ss-howto-title">HOW TO PLAY</div>
    <div class="ss-howto-body">
      <ol class="ss-howto-list">
        <li class="ss-howto-item">Streamer types <code>!start</code> to begin</li>
        <li class="ss-howto-item">A survey question appears on screen</li>
        <li class="ss-howto-item">Type your guess in chat (no command prefix)</li>
        <li class="ss-howto-item">Match a top-8 surveyed answer to score</li>
        <li class="ss-howto-item">Points = match count &times; 10 — popular answers pay more</li>
      </ol>
    </div>
  `;
  mount.appendChild(el);

  return {
    el,
    dispose() {
      el.remove();
    },
  };
}
